import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { UseFormRegister, UseFormReturn, useWatch } from "react-hook-form";

import attributeCore from "../../core/attribute.core";
import localeCore from "../../core/locale.core";
import { useTranslation } from "../../core/translation.core";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { ProductAttribute } from "../../types/product";
import { LocalInputDefaultLocale } from "../locale/locale.comp";
import { LabelAndInput } from "../reuseables/form.comp";
import { selectCss, selectCssNoMy } from "../reuseables/native.comp";
import DeleteIcon from "../../icons/delete.icon";

interface AttributeProps {
  methods: UseFormReturn<{ attributes: ProductAttribute[] }, any>;
}

export default function ProductAttributeEditor(props: AttributeProps) {
  const attributes = useWatch({
    name: "attributes",
    control: props.methods.control,
  });

  const { t, msg } = useTranslation();

  function addRow(e: any) {
    e.preventDefault();
    let attributes = props.methods.getValues("attributes");

    if (attributes === undefined) {
      attributes = [];
    }

    props.methods.setValue("attributes", [
      ...attributes,
      { attributeId: "", name: "", type: "string", value: { en: "", zhHans: "", zhHant: "" } },
    ]);
  }

  const rows = attributes?.map((productAttr, index) => (
    <AttributeRow key={index} productAttr={productAttr} methods={props.methods} idx={index} />
  ));

  return (
    <div className="space-y-4">
      <button id="add-more" type="button" className="underline" onClick={(e) => addRow(e)}>
        {t(msg.product.addAttributes)}
      </button>

      <div id="attributes-list" className="space-y-2">
        {rows}
      </div>
    </div>
  );
}

function useAttributes() {
  return useQuery({
    queryKey: ["attributes"],
    queryFn: attributeCore.listAttributes,
  });
}

interface AttributeRowProps {
  productAttr: ProductAttribute;
  methods: UseFormReturn<{ attributes: ProductAttribute[] }, any>;
  idx: number;
}

function AttributeRow(props: AttributeRowProps) {
  const { data: attributes, error, isLoading, isError } = useAttributes();

  function removeRow() {
    props.methods.setValue(
      "attributes",
      props.methods.getValues("attributes").filter((_, i) => i !== props.idx)
    );
  }

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading) return <LoadingOvalCenterIcon />;

  return (
    <div className="grid grid-cols-1 gap-6 sm:gap-2 sm:grid-cols-[150px_auto_20px] items-start pb-3 border-b border-her-gray">
      <div>
        <p className="text-[10px] mb-1">#{props.idx + 1}</p>
        <select className={`${selectCssNoMy}`} {...props.methods.register(`attributes.${props.idx}.attributeId`)}>
          {attributes!.map((attr) => (
            <option key={attr.id} value={attr.id}>
              {localeCore.getLocaleValueDefaultFallback(attr.name)}
            </option>
          ))}
        </select>
      </div>
      <ValueBox register={props.methods.register} fieldName={`attributes.${props.idx}.value`} productAttr={props.productAttr} />
      <button onClick={removeRow} type="button" className="delete-btn mt-6">
        <DeleteIcon className="text-red-400 w-6 h-6 items-center dark:hover:text-red-200 hover:text-red-800" />
      </button>
    </div>
  );
}

interface ValueBoxProps {
  register: UseFormRegister<{ attributes: ProductAttribute[] }>;
  fieldName: string;
  productAttr: ProductAttribute;
}

const ValueBox = ({ register, fieldName, productAttr }: ValueBoxProps) => {
  const { t, msg } = useTranslation();
  const { data: attributes, isLoading, error, isError } = useAttributes();

  if (isError) return <ErrorCenterIcon reason={error.message} />;
  if (isLoading) return <LoadingOvalCenterIcon />;
  if (!attributes) return null;

  if (!productAttr) return null;

  const selectedAttr = attributes.find((a) => a.id === productAttr.attributeId);

  if (!selectedAttr) return null;

  switch (selectedAttr.type) {
    case "string":
      return (
        <LocalInputDefaultLocale
          register={register}
          label=""
          fieldName={fieldName}
          labelCss="hidden"
          subLabelCss="text-[10px] mb-1"
        />
      );

    case "number":
      return (
        <LabelAndInput
          type="number"
          label={t(msg.attribute.number)}
          register={register}
          fieldName={fieldName}
          topLabelCss="block"
          labelCss="block text-[10px] mb-1"
        />
      );
  }
};
